<template>
    <div style="z-index: 100;"  :class="{ 'fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center': isModal }" v-if="isVisible">
        <div
            :class="isModal ? 'relative bg-white rounded-lg w-full max-w-xl p-6 mx-4' : 'w-full max-w-md p-6 mx-auto mt-8 bg-white shadow-md rounded-lg'">

            <!-- Close Button untuk Modal -->
            <button v-if="isModal" @click="closeModal" class="absolute top-8 right-6 text-gray-500 focus:outline-none"
                :disabled="isLoadingSubmit || isLoadingOTP">
                <AppIcon icon="mdi:close" class="w-6 h-6" />
            </button>

            <!-- Title -->
            <h2 class="text-xl font-semibold mb-4">{{ $t('emailChange.title') }}</h2>
            <hr class="mb-4">

            <!-- Email Field -->
            <div class="mb-4">
                <BaseInput type="email" placeholder="New Email" v-model="form.newEmail"
                    :class="{ 'border-red-500': errors.newEmail }" :disabled="isOTPSent || isLoadingOTP || isLoadingSubmit" />
            </div>

            <!-- Password Field -->
            <div class="mb-4">
                <BaseInput type="password" placeholder="Password" v-model="form.password"
                    :class="{ 'border-red-500': errors.password }" :disabled="isLoadingSubmit || isLoadingOTP" />
            </div>

            <!-- OTP Input Field dan Button dalam Satu Baris -->
            <div class="flex items-center gap-2 mb-4">
                <!-- Verification Code Input -->
                <div class="flex-1">
                    <BaseInput type="text" placeholder="Verification Code" v-model="form.verificationCode"
                        :class="{ 'border-red-500': errors.verificationCode }"
                        :disabled="isLoadingSubmit || isLoadingOTP" />
                </div>

                <!-- Send OTP Button -->
                <div>
                    <button @click.prevent="triggerSendOTP"
                        class="py-2 px-4 mb-4 rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2" :class="{
                            'bg-green-500 text-white hover:bg-green-600 focus:ring-green-600': !isLoadingOTP && !isOTPTimerActive,
                            'bg-gray-300 text-gray-500 cursor-not-allowed': isLoadingOTP || isOTPTimerActive
                        }" :disabled="isLoadingOTP || !form.newEmail || isOTPTimerActive">
                        <span v-if="isLoadingOTP">{{ $t('emailChange.otpSending') }}</span>
                        <span v-else-if="isOTPTimerActive">{{ otpTimer }}s</span>
                        <span v-else>{{ $t('emailChange.sendOTP') }}</span>
                    </button>
                </div>
            </div>

            <!-- Submit Button -->
            <div class="flex flex-row justify-end">
                <button type="submit" @click.prevent="submitForm"
                    class="py-2 px-4 mt-4 rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2"
                    :class="{
                        'bg-blue-500 text-white': !isLoadingSubmit && isFormValid,
                        'bg-gray-300 text-gray-500 cursor-not-allowed': isLoadingSubmit || !isFormValid
                    }" :disabled="isLoadingSubmit || !isFormValid">
                    <span v-if="isLoadingSubmit">{{ $t('emailChange.submitLoading') }}</span>
                    <span v-else>{{ $t('emailChange.submitButton') }}</span>
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions } from 'vuex';
import { getResponseMessage } from "@/utils/responseMapping";

export default {
    name: "ChangeEmail",
    props: {
        isModal: {
            type: Boolean,
            default: false
        },
        isVisible: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                newEmail: '',
                password: '',
                verificationCode: ''
            },
            errors: {
                newEmail: '',
                password: '',
                verificationCode: ''
            },
            isLoadingOTP: false,
            isLoadingSubmit: false,
            isOTPSent: false,
            isOTPTimerActive: false,
            otpTimer: 180
        };
    },
    computed: {
        isFormValid() {
            return !!this.form.newEmail && !!this.form.password && !!this.form.verificationCode;
        }
    },
    methods: {
        ...mapActions('user', ['sendOTP', 'changeEmail']),

        validateForm() {
            this.errors.newEmail = !this.form.newEmail
                ? this.$t('emailChange.emailRequired')
                : '';
            this.errors.password = !this.form.password
                ? this.$t('emailChange.passwordRequired')
                : '';
            this.errors.verificationCode = !this.form.verificationCode
                ? this.$t('emailChange.verificationCodeRequired')
                : '';
        },

        closeModal() {
            this.form = { newEmail: '', password: '', verificationCode: '' };
            this.errors = { newEmail: '', password: '', verificationCode: '' };
            this.isLoadingSubmit = false;
            this.isLoadingOTP = false;
            this.isOTPSent = false;
            this.isOTPTimerActive = false;
            this.otpTimer = 180;
            this.$emit('close');
        },

        async triggerSendOTP() {
            if (!this.form.newEmail) {
                this.errors.newEmail = this.$t('emailChange.emailRequired');
                this.$alert.toast('warning', this.$t('emailChange.emailRequired'));
                return;
            }

            try {
                this.isLoadingOTP = true;
                const payload = {
                    Email: this.form.newEmail,
                    SendBy: 3,
                    Type: 3 // Change email
                };

                const response = await this.sendOTP(payload);
                this.$alert.toast('success', this.$t('emailChange.otpSent'));

                const timerDuration = response?.data.data || 300; // Default ke 180 detik jika 'data' tidak ada

                this.startOTPTimer(timerDuration);
            } catch (error) {
                this.$alert.toast('error', this.$t('emailChange.otpFailed'));
            } finally {
                this.isLoadingOTP = false;
            }
        },

        startOTPTimer(duration) {
            this.isOTPTimerActive = true;
            this.otpTimer = duration || 300;

            const interval = setInterval(() => {
                if (this.otpTimer > 0) {
                    this.otpTimer--;
                } else {
                    clearInterval(interval);
                    this.isOTPTimerActive = false;
                    this.isOTPSent = false;
                }
            }, 1000);
        },

        async submitForm() {
            this.validateForm();

            if (!this.errors.newEmail && !this.errors.password && !this.errors.verificationCode) {
                this.isLoadingSubmit = true;
                try {
                    const payload = {
                        NewEmail: this.form.newEmail,
                        Password: this.form.password,
                        VerificationCode: this.form.verificationCode
                    };

                    const response = await this.changeEmail(payload);
                    const successMessage = getResponseMessage(response.status, response.data.code);
                    this.$alert.toast('success', successMessage || this.$t('emailChange.success'));
                    this.closeModal();
                } catch (error) {
                    this.$alert.toast('error', this.$t('emailChange.error'));
                } finally {
                    this.isLoadingSubmit = false;
                }
            }
        }
    }
};
</script>